/** Values needed for a monetary summary of a BC settlement line. */
export type SettlementAmountLine = {
  systemId: string;
  amount: number | null;
  directUnitCost: number | null;
  quantity: number | null;
};

const roundMoney = (amount: number) => Math.round(amount * 100) / 100;

/**
 * Display BC's line amount (not unit price). When a user has an unsaved
 * change of directUnitCost, preview its effect on the existing amount.
 * BC remains the source of truth after save/reload.
 */
export function getSettlementLineAmount(
  line: SettlementAmountLine,
  original?: SettlementAmountLine,
): number {
  const bcAmount = Number(original?.amount ?? line.amount ?? line.directUnitCost ?? 0);
  if (!original || original.directUnitCost === line.directUnitCost) {
    return roundMoney(bcAmount);
  }

  const oldUnit = Number(original.directUnitCost ?? 0);
  const newUnit = Number(line.directUnitCost ?? 0);
  const quantity = Number(line.quantity ?? original.quantity ?? 1);
  return roundMoney(bcAmount + (newUnit - oldUnit) * quantity);
}

/** Sum every BC line for one settlement type and a possible unsaved new line. */
export function sumSettlementAmounts(
  lines: SettlementAmountLine[],
  originalLines: SettlementAmountLine[],
  pendingAmount?: number,
): number {
  const originals = new Map(originalLines.map((line) => [line.systemId, line]));
  const total = lines.reduce(
    (sum, line) => sum + getSettlementLineAmount(line, originals.get(line.systemId)),
    pendingAmount ?? 0,
  );
  return roundMoney(total);
}
